import React, { useEffect, useState } from "react";
import { Activity, RefreshCw, TrendingUp, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function SuperAdminHealthAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/health/audit");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const items = data?.top_actions || [];
  const maxCount = items[0]?.count || 1;

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6" data-testid="health-audit-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Activity className="text-violet-400" size={22} />
            Uso de Endpoints
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Top ações dos últimos {data?.window_days || 30} dias —{" "}
            <span className="text-violet-300 font-semibold">
              {data?.total_events?.toLocaleString("en-US") || 0}
            </span>{" "}
            eventos totais
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
          data-testid="health-audit-refresh"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading && !data && (
        <div className="text-center text-slate-400 py-10">Carregando…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center text-slate-400 py-10">
          Nenhum evento de audit registrado nos últimos 30 dias.
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[11px] uppercase tracking-widest text-slate-400 font-bold">
            <div className="col-span-1">#</div>
            <div className="col-span-5">Ação</div>
            <div className="col-span-2 text-right flex items-center justify-end gap-1">
              <TrendingUp size={11} /> Uso
            </div>
            <div className="col-span-4 text-right flex items-center justify-end gap-1">
              <Clock size={11} /> Último uso
            </div>
          </div>
          <ul>
            {items.map((row, i) => {
              const pct = Math.round((row.count / maxCount) * 100);
              return (
                <li
                  key={row.action}
                  className="border-t border-white/5 px-4 py-3 hover:bg-white/5 transition-colors"
                  data-testid={`health-audit-row-${row.action}`}
                >
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-1 text-slate-500 text-sm">
                      {i + 1}
                    </div>
                    <div className="col-span-7 sm:col-span-5 min-w-0">
                      <code className="text-sm font-mono text-violet-300 break-all">
                        {row.action}
                      </code>
                      <div className="mt-1 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="col-span-4 sm:col-span-2 text-right text-white font-bold tabular-nums">
                      {row.count.toLocaleString("en-US")}
                    </div>
                    <div className="hidden sm:block col-span-4 text-right text-xs text-slate-400">
                      {fmtDate(row.last_used)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
